import React, { useEffect, useMemo, useState } from 'react';
import { HardHat, Sparkles, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { useT } from '../utils/i18n';
const PHONE_NUMBER = '(206) 619-3742';
// Slogan pool — i18n keys, picked at random ONCE per page load (no in-page
// rotation). The bar reinforces brand, never promotes a discount or a
// per-customer state. Active-order tracking lives in the Hero WelcomeBack
// card now, where it's contextual to the customer's session.
const SLOGAN_KEYS = ['announce.slogan.sameDay', 'announce.slogan.bulkDelivery', 'announce.slogan.direct', 'announce.slogan.familyOwned', 'announce.slogan.realTrucks', 'announce.slogan.tacoma'] as const;
export function AnnouncementBar() {
  const location = useLocation();
  const t = useT();
  const isContractors = location.pathname.includes('subcontractors') || location.pathname.includes('contractors');
  const [phoneOpen, setPhoneOpen] = useState(false);
  useEffect(() => {
    if (!phoneOpen) return;
    const id = setTimeout(() => setPhoneOpen(false), 3000);
    return () => clearTimeout(id);
  }, [phoneOpen]);
  // Pick a single slogan key per page load. Lazy initializer keeps the
  // choice stable for the session — fresh on each visit without
  // distracting in-page rotation.
  const [sloganKey] = useState<string>(() => SLOGAN_KEYS[Math.floor(Math.random() * SLOGAN_KEYS.length)]);
  const currentSlide = useMemo(() => ({
    key: sloganKey,
    content: <span className="flex items-center gap-1.5 min-w-0" data-id="element-159">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/10 text-amber-300 mr-1 shrink-0" data-id="element-160">
            <Sparkles className="w-3 h-3" data-id="element-161" />
          </span>
          <span className="hidden sm:inline text-[15px] font-semibold" data-id="element-162">
            {t(sloganKey)}
          </span>
          <span className="sm:hidden text-[12px] font-semibold truncate" data-id="element-163">
            {t(sloganKey)}
          </span>
        </span>
  }), [sloganKey, t]);
  return <div className="text-white py-1.5 px-4 text-xs sm:text-sm font-medium relative z-[60] border-b border-[#57534B]/60 bg-[#534944]" data-id="element-164">
      {/*
                                Mobile: justify-between keeps the right-side items pinned to the
                                right edge so they don't shift when the slogan length changes.
                                Desktop: centered.
                              */}
      <div className="max-w-7xl mx-auto flex items-center justify-between md:justify-center gap-3 sm:gap-4" data-id="element-165">
        {/* Left slot — slogan only. Active-order tracking moved to the
            Hero WelcomeBack card so it's adjacent to the customer's
            real session context (name, last order, reorder action). */}
        <div className="flex-1 md:flex-none min-w-0 flex items-center min-h-[20px] relative" data-id="element-166">
          <AnimatePresence mode="wait" data-id="element-167">
            <motion.div key={currentSlide.key} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.35,
            ease: 'easeOut'
          }} className="flex items-center whitespace-nowrap min-w-0 max-w-full" data-id="element-168">
              {currentSlide.content}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Right group — stays static */}
        <div className="flex items-center gap-2 sm:gap-4 shrink-0" data-id="element-169">
          <span className="opacity-40" data-id="element-170">|</span>

          {/* Phone — desktop: icon + number always visible.
           Mobile: icon-only; number slides out on tap, hides after 3s. */}
          <a href="tel:206-619-3742" aria-label={`Call ${PHONE_NUMBER}`} className="hidden md:inline-flex items-center gap-1.5 px-2 h-7 rounded-full hover:bg-white/10 transition-colors text-white font-semibold text-sm" data-id="element-171">
            <Phone className="w-4 h-4" data-id="element-172" />
            <span data-id="element-173">{PHONE_NUMBER}</span>
          </a>
          <div className="md:hidden relative flex items-center text-white" onClick={() => setPhoneOpen(true)} data-id="element-174">
            <a href="tel:206-619-3742" aria-label={`Call ${PHONE_NUMBER}`} className="flex items-center justify-center w-7 h-7 rounded-full hover:bg-white/10 transition-colors" data-id="element-175">
              <Phone className="w-4 h-4" data-id="element-176" />
            </a>
            <AnimatePresence data-id="element-177">
              {phoneOpen && <motion.span initial={{
              opacity: 0,
              x: -8,
              width: 0
            }} animate={{
              opacity: 1,
              x: 0,
              width: 'auto'
            }} exit={{
              opacity: 0,
              x: -8,
              width: 0
            }} transition={{
              duration: 0.28,
              ease: [0.22, 1, 0.36, 1]
            }} className="ml-1.5 text-white font-semibold tracking-tight text-xs whitespace-nowrap pointer-events-none overflow-hidden" data-id="element-178">
                  {PHONE_NUMBER}
                </motion.span>}
            </AnimatePresence>
          </div>

          <span className="opacity-40" data-id="element-179">|</span>

          <Link to="/subcontractors" className={`flex items-center transition-colors font-[600] ${isContractors ? 'text-amber-300 hover:text-amber-200' : 'text-white hover:text-white/80'}`} data-id="element-180">
            <HardHat className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" data-id="element-181" />
            {t('announce.contractors')}
          </Link>
        </div>
      </div>
    </div>;
}